/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApiErrorResponse } from "@/_models/common";
import { AxiosError } from "axios";
import { blocksToText } from "./santity";

// TODO: we might complete this later
// const errorList = {
//   ERROR001: "Invalid email or password",
//   ERROR002: "Account already exists",
//   ERROR003: "Email already exists",
//   ERROR004: "Password and confirm password do not match",
//   ERROR005: "Invalid verification code",
//   ERROR006: "Incorrect password",
//   ERROR007: "Account with email does not exist",
//   ERROR008: "User not verified/not active yet",
//   ERROR009: "User cannot resend code now! retry after 2 minutes",
//   ERROR500: "Undefined error",
//   ERROR106: "User already verified",
// };

export const getMessageFromAxiosError = (error: unknown, getCode = false) => {
  const axiosErrorResponse = (error as AxiosError<ApiErrorResponse>).response;
  const msg = axiosErrorResponse?.data.message;
  const code = axiosErrorResponse?.data.statusCode;
  if (getCode) {
    return String(code) ?? null;
  }
  return msg ?? null;
};

export const setStorageData = (key: string, data: unknown) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(key, JSON.stringify(data));
  }
};

export const getStorageData = <T>(key: string) => {
  if (typeof window === "undefined") {
    return null;
  }
  const data = localStorage.getItem(key);
  return data ? (JSON.parse(data) as T) : null;
};

export const removeStorageData = (key: string) => {
  localStorage.removeItem(key);
};

export const blogLinkBuilder = (
  slug: string,
  isPublicBlog = true,
  id?: string,
) => {
  if (!isPublicBlog && id) {
    return `/discover/blog/${id}`;
  }
  return `/blog/${slug}`;
};

export const sanity = {
  blocksToText,
};

export const getLocaleFromPath = (path: string) => {
  const match = path.match(/^\/(en|vi)(\/|$)/);
  return match?.[1];
};

/**
 * Sanitizes a search query by cleaning and normalizing the input text.
 *
 * This function performs the following operations:
 * 1. Trims leading and trailing whitespace
 * 2. Replaces newlines with single spaces
 * 3. Normalizes multiple consecutive spaces to single spaces
 * 4. Removes special symbols and characters (keeps alphanumeric, Vietnamese characters, hyphens, and spaces)
 * 5. Limits the query length to 500 characters
 *
 * @param query - The raw search query string to sanitize
 * @returns A cleaned and normalized search query string
 *
 * @example
 * ```typescript
 * const rawQuery = "  Hello World!@#$%^&*()\n\nHow are you?  ";
 * const sanitized = sanitizeQuery(rawQuery);
 * // Result: "Hello World! How are you?"
 *
 * // Usage in search components
 * const handleSearch = (input: string) => {
 *   const cleanQuery = sanitizeQuery(input);
 *   performSearch(cleanQuery);
 * };
 * ```
 */
export const sanitizeQuery = (query: string): string => {
  return query
    .trim()
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .replace(
      /[^\w\s\u00C0-\u017F\u0100-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF-]/g,
      "",
    )
    .substring(0, 500);
};

export const randomId = () => {
  return Math.random().toString(36).substring(2, 15);
};

/**
 * Generates a random percentage value within a specified range.
 *
 * This function creates a random integer between the minimum and maximum values (inclusive).
 * Useful for generating fake data or placeholder values in UI components.
 *
 * @param min - The minimum value (inclusive)
 * @param max - The maximum value (inclusive)
 * @returns A random integer between min and max
 *
 * @example
 * ```typescript
 * // Generate a percentage between 70-90%
 * const percentage = generateRandomPercentage(70, 90);
 * // Result: 75, 82, 89, etc.
 *
 * // Usage in components
 * const fakeData = {
 *   mustHave: `${generateRandomPercentage(70, 90)}%`,
 *   niceToHave: `${generateRandomPercentage(60, 80)}%`,
 * };
 * ```
 */
export const generateRandomPercentage = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Converts a snake_case string to camelCase.
 *
 * This function transforms snake_case strings by:
 * 1. Splitting on underscores
 * 2. Capitalizing the first letter of each word except the first
 * 3. Joining all words together
 *
 * @param str - The snake_case string to convert
 * @returns The camelCase version of the input string
 *
 * @example
 * ```typescript
 * // Basic conversion
 * const result = snakeToCamel("user_name");
 * // Result: "userName"
 *
 * // Multiple underscores
 * const result2 = snakeToCamel("first_name_last_name");
 * // Result: "firstNameLastName"
 *
 * // Single word (no underscores)
 * const result3 = snakeToCamel("username");
 * // Result: "username"
 *
 * // Usage in data transformation
 * const apiData = { user_id: 123, created_at: "2024-01-01" };
 * const transformedData = Object.keys(apiData).reduce((acc, key) => {
 *   acc[snakeToCamel(key)] = apiData[key];
 *   return acc;
 * }, {});
 * // Result: { userId: 123, createdAt: "2024-01-01" }
 * ```
 */
export const snakeToCamel = (str: string): string => {
  return str
    .split("_")
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("");
};

/**
 * Converts an object with snake_case keys to camelCase keys.
 *
 * This function recursively transforms all keys in an object from snake_case to camelCase.
 * It handles nested objects and arrays, preserving the structure while converting keys.
 *
 * @param obj - The object with snake_case keys to convert
 * @returns A new object with camelCase keys
 *
 * @example
 * ```typescript
 * // Basic object conversion
 * const apiResponse = {
 *   user_id: 123,
 *   user_name: "john_doe",
 *   created_at: "2024-01-01",
 *   profile_data: {
 *     first_name: "John",
 *     last_name: "Doe"
 *   }
 * };
 *
 * const transformed = snakeToCamelKeys(apiResponse);
 * // Result: {
 * //   userId: 123,
 * //   userName: "john_doe",
 * //   createdAt: "2024-01-01",
 * //   profileData: {
 * //     firstName: "John",
 * //     lastName: "Doe"
 * //   }
 * // }
 * ```
 */
export const snakeToCamelKeys = <T = Record<string, any>>(obj: unknown): T => {
  if (obj === null || obj === undefined) {
    return obj as T;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => snakeToCamelKeys(item)) as T;
  }

  if (typeof obj === "object") {
    return Object.keys(obj).reduce(
      (acc, key) => {
        const camelKey = snakeToCamel(key);
        acc[camelKey] = snakeToCamelKeys(obj[key as keyof typeof obj]);
        return acc;
      },
      {} as Record<string, unknown>,
    ) as T;
  }

  return obj as T;
};

export const containsUidInPath = (path: string) => {
  const regex = /(\w{8})-(\w{4})-(\w{4})-(\w{4})-(\w{12})$/;
  return regex.test(path);
};

export const updateQueryParams = (params: Record<string, string>) => {
  const searchParams = new URLSearchParams(window.location.search);
  Object.entries(params).forEach(([key, value]) => {
    searchParams.set(key, value);
  });
  window.history.pushState(
    {},
    "",
    `${window.location.pathname}?${searchParams.toString()}`,
  );
};

/**
 * Unicode-safe base64 encoding.
 * Converts a string containing Unicode characters (e.g., Vietnamese) to base64.
 * Uses TextEncoder to handle UTF-8 encoding properly.
 *
 * @param str - The string to encode
 * @returns Base64 encoded string
 *
 * @example
 * ```typescript
 * const encoded = encodeBase64("Tìm React Developer");
 * // Works with Vietnamese and other Unicode characters
 * ```
 */
export const encodeBase64 = (str: string): string => {
  if (typeof window === "undefined") {
    return Buffer.from(str, "utf-8").toString("base64");
  }
  const encoder = new TextEncoder();
  const bytes = encoder.encode(str);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Unicode-safe base64 decoding.
 * Converts a base64 string back to a Unicode string.
 * Uses TextDecoder to handle UTF-8 decoding properly.
 *
 * @param base64 - The base64 string to decode
 * @returns Decoded Unicode string
 *
 * @example
 * ```typescript
 * const decoded = decodeBase64("VGltIFJlYWN0IERldmVsb3Blcg==");
 * // Returns: "Tìm React Developer"
 * ```
 */
export const decodeBase64 = (base64: string): string => {
  if (typeof window === "undefined") {
    return Buffer.from(base64, "base64").toString("utf-8");
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  const decoder = new TextDecoder();
  return decoder.decode(bytes);
};

export { formatNumberWithCommas } from "./numberUtils";
